import {
  mergeAiQuestions as mergeAiQuestionsWithHistory,
  setAiQuestionDisposition as setAiQuestionDispositionInList,
  updateAiQuestionAnswer as updateAiQuestionAnswerInList,
} from "./kbAdminAiHelpers";
import type { AiQuestionAnswer, AppliedSuggestion, PanelNote, RelatedArticlePreview } from "./types";

type ShowAiFeedback = (note: PanelNote | null) => void;

type Params = {
  dictation: { isListening: boolean; draft: string; stop: () => void; reset: () => void };
  aiBodyDraft: string;
  articleContent: string;
  roleSetters: {
    setAiPurpose: (v: any) => void;
    setAiGeneratedNotes: (v: string[]) => void;
    setAiResolvedFacts: (v: string[]) => void;
    setAiPlacementSummary: (v: string) => void;
    setAiSuggestedAction: (v: any) => void;
    setAiTopicComplexity: (v: any) => void;
    setAiRecommendedDocCount: (v: number) => void;
    setAiStopReason: (v: any) => void;
    setAiFollowOnDocs: (v: AppliedSuggestion["follow_on_docs"]) => void;
    setAiKbGapCandidates: (v: AppliedSuggestion["kb_gap_candidates"]) => void;
    setAiArticleLinkSuggestions: (v: AppliedSuggestion["article_link_suggestions"]) => void;
    setAiRelatedArticles: (v: AppliedSuggestion["related_articles"]) => void;
    setAiQuestionAnswers: (u: any) => void;
    setAiBodyDraft: (v: string) => void;
    setAiBodyGenerationNotes: (v: string[]) => void;
    setPreviousArticleContent: (v: string | null) => void;
    setRelatedPreview: (v: RelatedArticlePreview | null) => void;
    setRelatedPreviewLoading: (v: boolean) => void;
    setAiLinksOpen: (v: boolean) => void;
    setAiGapsOpen: (v: boolean) => void;
    setAppliedSuggestion: (v: AppliedSuggestion | null) => void;
    setAiFeedback: (v: PanelNote | null) => void;
    setArticleContent: (u: any) => void;
  };
  previousArticleContent: string | null;
  showAiFeedback: ShowAiFeedback;
};

export function useKbAiSessionHelpers(p: Params) {
  const s = p.roleSetters;
  function resetAiSession(options?: { keepPurpose?: boolean }) {
    if (!options?.keepPurpose) s.setAiPurpose("");
    s.setAiGeneratedNotes([]); s.setAiResolvedFacts([]); s.setAiPlacementSummary(""); s.setAiSuggestedAction(""); s.setAiTopicComplexity("");
    s.setAiRecommendedDocCount(1); s.setAiStopReason(""); s.setAiFollowOnDocs([]); s.setAiKbGapCandidates([]); s.setAiArticleLinkSuggestions([]);
    s.setAiRelatedArticles([]); s.setAiQuestionAnswers([]); s.setAiBodyDraft(""); s.setAiBodyGenerationNotes([]); s.setPreviousArticleContent(null);
    s.setRelatedPreview(null); s.setRelatedPreviewLoading(false); s.setAiLinksOpen(false); s.setAiGapsOpen(false); s.setAppliedSuggestion(null); s.setAiFeedback(null);
  }

  function mergeAiQuestions(nextQuestions: string[]) { s.setAiQuestionAnswers((previous: AiQuestionAnswer[]) => mergeAiQuestionsWithHistory(previous, nextQuestions)); }
  function updateAiQuestionAnswer(question: string, answer: string) { s.setAiQuestionAnswers((previous: AiQuestionAnswer[]) => updateAiQuestionAnswerInList(previous, question, answer)); }
  function setAiQuestionDisposition(question: string, disposition: AiQuestionAnswer["disposition"]) { s.setAiQuestionAnswers((previous: AiQuestionAnswer[]) => setAiQuestionDispositionInList(previous, question, disposition)); }

  function applyAiBodyDraft(mode: "replace" | "append") {
    if (!p.aiBodyDraft.trim()) return;
    s.setPreviousArticleContent(p.articleContent);
    if (mode === "replace") {
      s.setArticleContent(p.aiBodyDraft);
      p.showAiFeedback({ tone: "success", title: "AI draft applied", text: "The generated body draft replaced the current article body. Use restore if you want to undo it." });
      return;
    }
    s.setArticleContent((previous: string) => (previous.trim() ? `${previous.trim()}\n\n${p.aiBodyDraft}` : p.aiBodyDraft));
    p.showAiFeedback({ tone: "success", title: "AI draft appended", text: "The generated draft was appended to the current article body." });
  }

  function restorePreviousBody() {
    if (p.previousArticleContent == null) return;
    s.setArticleContent(p.previousArticleContent);
    p.showAiFeedback({ tone: "info", title: "Body restored", text: "The article body was restored to the version from before the AI draft was applied." });
  }

  function applyDictationToPurpose() {
    const text = (p.dictation.draft || "").trim();
    if (!text) return;
    s.setAiPurpose((previous: string) => (previous.trim() ? `${previous.trim()} ${text}` : text));
    p.showAiFeedback({ tone: "info", title: "Voice prompt added", text: "The spoken text was added to the document purpose / AI prompt field." });
    if (p.dictation.isListening) p.dictation.stop();
    window.setTimeout(() => p.dictation.reset(), 0);
  }

  return { resetAiSession, mergeAiQuestions, updateAiQuestionAnswer, setAiQuestionDisposition, applyAiBodyDraft, restorePreviousBody, applyDictationToPurpose };
}
